import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// Types
export type ReleaseChannel = 'stable' | 'beta'

export interface VersionInfo {
  release_date: string
  requires_php?: string
  requires_wp?: string
  changelog?: string
  downloads?: number
  channel?: ReleaseChannel
}

export interface Plugin {
  slug: string
  name: string
  type: 'plugin' | 'theme'
  current_version: string
  requires_license: boolean
  author: string
  homepage?: string
  requires_php: string
  requires_wp: string
  tested_wp: string
  last_updated: string
  sections: {
    description: string
    installation?: string
    changelog: string
    faq?: string
  }
  versions: Record<string, VersionInfo>
  channels?: {
    stable: string
    beta?: string
  }
  total_downloads?: number
}

export interface CreatePluginData {
  slug: string
  name: string
  type?: 'plugin' | 'theme'
  current_version: string
  requires_license?: boolean
  author?: string
  requires_php?: string
  requires_wp?: string
  tested_wp?: string
  sections?: {
    description?: string
    changelog?: string
  }
}

export interface UpdatePluginData {
  slug: string
  name?: string
  author?: string
  requires_php?: string
  requires_wp?: string
  tested_wp?: string
  requires_license?: boolean
  sections?: {
    description?: string
    changelog?: string
  }
}

// Query keys
export const pluginKeys = {
  all: ['plugins'] as const,
  lists: () => [...pluginKeys.all, 'list'] as const,
  list: () => [...pluginKeys.lists()] as const,
  details: () => [...pluginKeys.all, 'detail'] as const,
  detail: (slug: string) => [...pluginKeys.details(), slug] as const,
}

// Hooks
export function usePlugins() {
  return useQuery({
    queryKey: pluginKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<{ plugins: Plugin[] }>('/api/update-server/plugins')
      return response.plugins || []
    },
  })
}

export function usePlugin(slug: string) {
  return useQuery({
    queryKey: pluginKeys.detail(slug),
    queryFn: async () => {
      // API returns {success, plugin, available_versions, download_stats}
      const response = await apiClient.get<{
        success: boolean
        plugin: Plugin
        available_versions: string[]
        download_stats: { total: number }
      }>(`/api/update-server/plugins/${slug}`)
      // Merge download stats into plugin for convenience
      return {
        ...response.plugin,
        total_downloads: response.download_stats?.total,
      }
    },
    enabled: !!slug,
  })
}

export function useCreatePlugin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreatePluginData) => {
      return apiClient.post<Plugin>('/api/update-server/plugins', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pluginKeys.lists() })
    },
  })
}

export function useUpdatePlugin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdatePluginData) => {
      return apiClient.post<Plugin>('/api/update-server/plugins', data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pluginKeys.lists() })
      queryClient.invalidateQueries({ queryKey: pluginKeys.detail(variables.slug) })
    },
  })
}

export function useDeletePlugin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (slug: string) => {
      return apiClient.delete(`/api/update-server/plugins/${slug}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pluginKeys.lists() })
    },
  })
}

export interface PromoteVersionData {
  slug: string
  version: string
  toChannel: ReleaseChannel
}

export interface PromoteVersionResponse {
  message: string
  version: string
  from_channel: ReleaseChannel
  to_channel: ReleaseChannel
  channels: {
    stable: string
    beta?: string
  }
}

export function usePromoteVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ slug, version, toChannel }: PromoteVersionData) => {
      return apiClient.post<PromoteVersionResponse>(
        `/api/update-server/plugins/${slug}/promote`,
        { version, to: toChannel }
      )
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pluginKeys.lists() })
      queryClient.invalidateQueries({ queryKey: pluginKeys.detail(variables.slug) })
    },
  })
}
